import {Capacitor} from "@capacitor/core";
import {QueryClient} from "@tanstack/react-query";
import {config} from "./config/config";

export function apiCall(path: apiMethods | apiGetMethods, params = {}, initConfig: any = {}) {
    const platform = (config.isCordova ? (Capacitor.getPlatform() || 'mobile').toLowerCase() : 'web');

    return fetch(config.apiUrl + '/' + String(path) + (document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''), {
        method: "POST",
        body:   JSON.stringify(params),

        ...initConfig,

        headers: {
            ...(initConfig.hasOwnProperty('body') ? {} : {'Content-Type': 'application/json'}), // custom body format
            'Accept':          'application/json',
            'x-firebase-auth': api.user?.['stsTokenManager']?.accessToken,
            'x-jwt-token':     api.jwt_token,
            'x-user-info':     (api.user?.uid || '-') + ':' + config.version + ':' + platform,

            ...(initConfig.headers || {}),
        },
    });
}

const api = {
    jwt_token: '',
    user:      undefined as any,
}

type apiMethods =
    'contact'
    | 'delete-account'
    | 'applications-create'

    | 'tournaments-create'
    | 'tournaments-update'
    | 'tournaments-delete'
    | 'tournaments-schedule-create'
    | 'tournaments-schedule-clear'
    | 'tournaments-complete'

    | 'leagues-create'
    | 'leagues-update'
    | 'leagues-delete'

    | 'players-create'
    | 'players-update'
    | 'players-delete'

    | 'games-create'
    | 'games-update'
    | 'games-delete'
    | 'games-invite'

    | 'posts-create'
    | 'posts-update'
    | 'posts-delete'

    | 'teams-create'
    | 'teams-update'
    | 'teams-delete'
    | 'teams-search'

    | 'auth/me'
    | 'auth/refresh'
    | 'auth/login'
    | 'auth/logout'
    | 'auth/makeSureUserExists'
    | 'auth/roleToggle'
    | 'auth/passwordResetLink'
    | 'auth/passwordResetConfirm'

    | 'signup'
    | 'confirm-email'
    | 'confirm-email-resend'
    | 'users-update'
    | 'players-search'
    | 'geo-search'
    | 'event-apply'
    | 'leagues-invite'
    | 'applications-update'
    | 'league-change-status'
    | 'league-schedule-create'
    | 'league-schedule-delete'

    | 'ranking-groups-update'
    | 'ranking-groups-delete'
    | 'ranking-groups-create'

    | 'courts-update'
    | 'courts-delete'
    | 'courts-create'
    | 'booking-book'
    | 'booking-update'
    | 'booking-delete'
    | 'booking-block'
    | 'booking-cancel'

    | 'tournaments-invite'
    | 'tournament-change-status'
    | 'tournament-schedule-create'
    | 'tournament-schedule-delete'
    | 'tournament-draw-create'
    | 'tournament-draw-delete'
    | 'tournament-stage-confirm'

    | 'submit-questionnaire'
    | 'sponsors-upload'
    | 'tournament-teams-create'

    | 'articles-create'
    | 'articles-delete'

    | 'stripe-prepare-payment'
    | 'messages-create-test'
    | 'submit-feature-request'
    | 'invite-player-by-email'
    | 'players-merge'
    | 'clubs-players-invite'
    | 'clubs-update'
    | 'clubs-create'
    | 'communities-update'
    | 'communities-create'
    | 'in-app/register-subscription'
    ;
type apiGetMethods =
    'tournaments-get'
    | 'tournaments-find'
    | 'tournaments-ranking'
    | 'tournaments-players-unassigned'

    | 'leagues-find'
    | 'leagues-get'
    | 'leagues-teams'
    | 'leagues-players-unassigned'
    | 'leagues-stats'
    | 'leagues-ranking'

    | 'players-get'
    | 'players-find'
    | 'games-get'
    | 'games-find'
    | 'applications-get'
    | 'teams-get'
    | 'teams-find'
    | 'teams-update'
    | 'teams-delete'
    | 'messages-get'

    | 'ranking-groups-get'
    | 'ranking-groups-find'

    | 'courts-get'
    | 'courts-find'
    | 'booking-available-courts'
    | 'booking-get'

    | 'player-profile'
    | 'organizer-profile'
    | 'public-player-profile'

    | 'users-get'
    | 'users-find'
    | 'tournament-teams'
    | 'tournament-stages'

    | 'marketplace-offers'
    | 'articles-get'
    | 'posts-get'
    | 'posts-find'
    | 'public-blog'
    | 'subscriptions-get'
    | 'upcoming-games'
    | 'clubs-get'
    | 'clubs-find'
    | 'communities-get'
    | 'communities-find'
    | 'global-ranking-league-stats'
    | 'pricing-plans'
    | 'players-merge'
    | 'socialite/google/url'
    ;

/**
 * @deprecated
 */
export function apiRetrieve(path: apiGetMethods, params = {}, initConfig = {}) {
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                obj.error = obj.message;
                return Promise.resolve(obj); // TODO: use Promise.reject(obj)
            })
        } else {
            return response.json();
        }
    });
}

export function apiRetrieveImproved(path: apiGetMethods, params = {}, initConfig = {}) {
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                return Promise.reject(obj);
            })
        } else {
            return response.json();
        }
    });
}

/**
 * @deprecated
 */
export function apiAction(path: apiMethods, params: any = {}, initConfig: any = {}) {
    if (path.endsWith('-update') && !params['values']?.['version'] && !params['version']) {
        throw new Error('Error: version not set on the model');
    }
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                obj.error = obj.message;
                return Promise.resolve(obj);
            })
        } else {
            return response.json();
        }
    });
}

export function apiActionImproved(path: apiMethods, params: any = {}, initConfig: any = {}) {
    if (path.endsWith('-update') && !params['values']?.['version'] && !params['version']) {
        throw new Error('Error: version not set on the model');
    }
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                return Promise.reject(obj);
            })
        } else {
            return response.json();
        }
    });
}

export function updateQueryDataItem(queryClient: QueryClient, queryKey: any, values: any) {
    queryClient.setQueryData(queryKey, (data: any) => {
        if (!data) {
            console.debug('[updateQueryData] ', JSON.stringify(queryKey), ` not found`);
            return data;
        }
        return {...data, ...values};
    });
}

export function updateQueryData(queryClient: QueryClient, queryKey: any, uid: string, values: any) {
    queryClient.setQueryData(queryKey, (data: any) => {
        if (!data) {
            return data;
        }
        if (data.uid === uid) {
            return {...data, ...values};
        }
        return data.map((oldData: any) => {
            if (oldData.uid === uid) {
                console.debug('[updateQueryData] ', JSON.stringify(queryKey), `[uid=${uid}] :=`, JSON.stringify(values));
                return {
                    ...oldData,
                    ...values,
                };
            }
            return oldData;
        });
    });
}

export default api;
